import { IAgentProfileSummaryView } from '@commenda/api';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupCardItem,
  ScrollArea,
} from '@commenda/kakashi';
import { AgentProfileHelper } from '@commenda/utils';
import { useState } from 'react';

interface AgentProfilePickerModalProps {
  profiles: IAgentProfileSummaryView[];
  selectedProfileKey: string | undefined;
  onClose: () => void;
  onSelect: (profileKey: string | undefined) => void;
}

export const AgentProfilePickerModal = ({
  profiles,
  selectedProfileKey,
  onClose,
  onSelect,
}: AgentProfilePickerModalProps) => {
  const [pickedKey, setPickedKey] = useState(selectedProfileKey ?? '');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="flex-col items-start gap-xs">
          <DialogTitle>Choose an expert</DialogTitle>
          <DialogDescription>
            The step runs as this profile: its instructions, its skills, and the
            tools it is granted.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-lg p-3xl">
          {/* DialogContent caps its height but does not scroll, so the scroller is ours. */}
          <ScrollArea className="max-h-[60vh]">
            <RadioGroup
              value={pickedKey}
              onValueChange={setPickedKey}
              className="flex flex-col gap-md"
            >
              {profiles.map((profile) => (
                <RadioGroupCardItem
                  key={profile.profileKey}
                  value={profile.profileKey}
                  cardClassName="items-start"
                  // Lines the control up with the first line of the name rather than the card's top
                  // edge, which items-start would otherwise do.
                  className="mt-0.5"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-sm">
                    <div className="flex flex-wrap items-center gap-sm">
                      <span className="txt-small-medium text-text-primary">
                        {AgentProfileHelper.getDisplayName(profile)}
                      </span>
                      {profile.owner ? (
                        <Badge variant="gray" size="sm">
                          {profile.owner}
                        </Badge>
                      ) : null}
                    </div>
                    <span className="txt-extra-small-regular font-mono break-all text-text-tertiary">
                      {profile.profileKey}
                    </span>
                    <div className="flex flex-col gap-xxs">
                      <span className="txt-extra-small-medium text-text-tertiary">
                        What it does
                      </span>
                      <p className="txt-extra-small-regular whitespace-pre-wrap text-text-secondary">
                        {profile.description}
                      </p>
                    </div>
                    <div className="flex flex-col gap-xxs">
                      <span className="txt-extra-small-medium text-text-tertiary">
                        What it hands back
                      </span>
                      <p className="txt-extra-small-regular whitespace-pre-wrap text-text-secondary">
                        {profile.output}
                      </p>
                    </div>
                  </div>
                </RadioGroupCardItem>
              ))}
            </RadioGroup>
          </ScrollArea>
        </div>

        <DialogFooter className="justify-between">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={() => {
              onSelect(undefined);
              onClose();
            }}
          >
            Clear selection
          </Button>
          <div className="flex items-center gap-md">
            <DialogClose asChild>
              <Button variant="secondary" size="sm" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              size="sm"
              type="button"
              disabled={!pickedKey}
              onClick={() => {
                onSelect(pickedKey);
                onClose();
              }}
            >
              Use this expert
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
